import React, { useState, useEffect, useRef, useCallback } from "react";
import Styles from "./ContractCalculator.module.css";
import { FileDownload, ArrowRightAlt, ChevronRight, ExpandMore } from "@mui/icons-material";
import ReactECharts from "echarts-for-react";
import {
  getCumulativeOverview,
  getCumulativeSupply,
  getCumulativeReturn,
  getCumulativeSupplySources,
  getCumulativeSupplyDestinations,
} from "../../Services/banking/BankingCalc";

/* ── Constants ──────────────────────────────────────────── */
const GROUP_BY_OPTIONS = [
  { key: "block", label: "Time Block" },
  { key: "day", label: "Day" },
  { key: "week", label: "Week" },
  { key: "month", label: "Month" },
];

const CONTRACT_COLORS = ["#ff9800", "#2E90FA", "#9c27b0", "#26a69a", "#f44336", "#607d8b"];

// Ported from insights ReMixChart — shared axis/brush accent color.
const SECONDARY_COLOR = "#34656D";
const AXIS_LINE_COLOR = "#E0E0E0";
const AXIS_LABEL_COLOR = "#8C8A96";

// Series colors — original domain palette (orange = actual, blue = scheduled).
const SERIES_COLORS = {
  actual: "#ff9800",
  scheduled: "#2E90FA",
};

/* ── Helpers ────────────────────────────────────────────── */
const fmt = (val, dec = 2) => {
  if (val == null || val === "") return "—";
  return Number(val).toLocaleString("en-IN", {
    minimumFractionDigits: dec,
    maximumFractionDigits: dec,
  });
};

// Compact y-axis value formatter (K / M / B) — ported from insights ReMixChart.
const formatAxisValue = (value) => {
  const absValue = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (absValue >= 1_000_000_000) {
    const v = absValue / 1_000_000_000;
    return sign + (v >= 10 ? v.toFixed(0) : v.toFixed(1)).replace(/\.0$/, "") + "B";
  } else if (absValue >= 1_000_000) {
    const v = absValue / 1_000_000;
    return sign + (v >= 10 ? v.toFixed(0) : v.toFixed(1)).replace(/\.0$/, "") + "M";
  } else if (absValue >= 1_000) {
    const v = absValue / 1_000;
    return sign + (v >= 10 ? v.toFixed(0) : v.toFixed(1)).replace(/\.0$/, "") + "K";
  } else if (absValue < 10 && absValue > 0) {
    if (absValue < 0.01) return value.toFixed(4);
    else if (absValue < 0.1) return value.toFixed(3);
    else if (absValue < 1) return value.toFixed(2);
    else return value.toFixed(1);
  } else if (value === 0) return "0";
  else if (value % 1 === 0) return sign + Math.round(absValue).toString();
  return value.toFixed(1);
};

const escapeHtml = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

/* Enhanced tooltip HTML — ported from insights ReMixChart (customTooltipBuilder):
   frosted-glass card, SNAPSHOT kicker, large title, per-series rows with a
   vertical gradient accent bar and a boxed unit pill. Fully self-contained via
   an inline <style> block so it needs no external CSS. */
const buildTooltipHTML = ({ title, subtitle, rows, unit, fontSize = 12 }) => {
  const items = rows
    .map((r) => {
      const value = Number(r.value ?? 0)
        .toFixed(2)
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      const color = escapeHtml(r.color);
      return `<div class="cc-tt-item" style="--accent:${color}"><span class="cc-tt-bar"></span><span class="cc-tt-name">${escapeHtml(
        r.name,
      )}</span><span class="cc-tt-vwrap"><span class="cc-tt-value">${value}</span><span class="cc-tt-unit">${escapeHtml(
        unit,
      )}</span></span></div>`;
    })
    .join("");

  const subtitleHtml = subtitle
    ? `<span class="cc-tt-sub">${escapeHtml(subtitle)}</span>`
    : "";

  const css = `<style>
.cc-tt-shell{padding:0;pointer-events:inherit}
.cc-tt{background:rgba(255,255,255,0.92);backdrop-filter:saturate(140%) blur(14px);-webkit-backdrop-filter:saturate(140%) blur(14px);box-shadow:0 1px 2px rgba(15,23,42,0.06),0 8px 24px -6px rgba(15,23,42,0.18),0 24px 48px -12px rgba(15,23,42,0.16);border:1px solid rgba(15,23,42,0.08);border-radius:14px;font-family:'Inter','SF Pro Text',system-ui,-apple-system,sans-serif;min-width:220px;max-width:360px;padding:14px 14px 10px;color:#334155;letter-spacing:-0.005em}
.cc-tt-header{display:flex;flex-direction:column;gap:8px;padding:0 2px 12px;margin-bottom:6px;border-bottom:1px dashed rgba(15,23,42,0.09)}
.cc-tt-kicker{display:inline-flex;align-items:center;gap:5px;font-size:${Math.max(fontSize - 3, 9)}px;font-weight:700;letter-spacing:0.12em;text-transform:uppercase;color:#64748b}
.cc-tt-kicker-dot{width:5px;height:5px;border-radius:50%;background:linear-gradient(135deg,#10b981,#059669);box-shadow:0 0 0 3px rgba(16,185,129,0.15)}
.cc-tt-title-stack{display:flex;align-items:baseline;gap:8px;min-width:0;flex-wrap:wrap}
.cc-tt-title{font-weight:650;font-size:${fontSize + 5}px;color:#334155;line-height:1.15;letter-spacing:-0.02em;font-variant-numeric:tabular-nums}
.cc-tt-sub{font-size:${fontSize}px;font-weight:500;color:#94a3b8;letter-spacing:-0.005em}
.cc-tt-content{display:flex;flex-direction:column;gap:0;margin:0 -4px;padding:2px 4px 0}
.cc-tt-item{position:relative;display:flex;align-items:center;gap:10px;padding:7px 8px 7px 10px;border-radius:8px}
.cc-tt-item+.cc-tt-item{box-shadow:inset 0 1px 0 rgba(15,23,42,0.04)}
.cc-tt-bar{flex-shrink:0;width:3px;height:18px;border-radius:2px;background:linear-gradient(180deg,var(--accent),color-mix(in srgb,var(--accent) 75%,#000));box-shadow:0 0 0 1px color-mix(in srgb,var(--accent) 25%,transparent),0 0 10px color-mix(in srgb,var(--accent) 40%,transparent)}
.cc-tt-name{flex:1;min-width:0;white-space:normal;overflow-wrap:anywhere;line-height:1.25;font-size:${fontSize}px;font-weight:500;color:#64748b}
.cc-tt-vwrap{display:inline-flex;align-items:baseline;gap:6px;margin-left:auto;padding-left:14px}
.cc-tt-value{font-variant-numeric:tabular-nums;font-feature-settings:"tnum" 1;font-weight:650;font-size:${fontSize + 2}px;color:#334155;line-height:1;letter-spacing:-0.02em}
.cc-tt-unit{font-size:${Math.max(fontSize - 1, 10)}px;font-weight:600;color:#64748b;letter-spacing:0.05em;text-transform:uppercase;align-self:center;transform:translateY(-1px);background:rgba(15,23,42,0.04);padding:1px 5px;border-radius:4px}
</style>`;

  return `<div class="cc-tt-shell"><div class="cc-tt"><div class="cc-tt-header"><span class="cc-tt-kicker"><span class="cc-tt-kicker-dot"></span>Snapshot</span><div class="cc-tt-title-stack"><span class="cc-tt-title">${escapeHtml(
    title,
  )}</span>${subtitleHtml}</div></div><div class="cc-tt-content">${items}</div></div></div>${css}`;
};

const extractArray = (res) => {
  const d = res?.data?.data;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.daily)) return d.daily;
  if (Array.isArray(d?.items)) return d.items;
  return [];
};

const extractMetrics = (res) => {
  const d = res?.data?.data;
  if (d && !Array.isArray(d) && d.metrics) return d.metrics;
  return null;
};

const toChartData = (data = []) =>
  data.map((b) => ({
    x: b.time_block ?? b.date,
    date: b.date ?? null,
    actual_mw: b.actual_mwh ?? 0,
    scheduled_mw: b.scheduled_mwh ?? 0,
  }));

const processBreakdown = (data = [], groupBy) => {
  if (groupBy === "block") {
    const map = {};
    data.forEach((b) => {
      const key = b.date;
      if (!key) return;
      if (!map[key]) {
        map[key] = {
          date: key,
          scheduled_mwh: 0,
          actual_mwh: 0,
          actual_gna: 0,
          actual_tgna: 0,
          approval_nos: [],
          blocks: [],
        };
      }
      const day = map[key];
      day.scheduled_mwh += b.scheduled_mwh ?? 0;
      day.actual_mwh += b.actual_mwh ?? 0;
      day.actual_gna += b.actual_gna ?? 0;
      day.actual_tgna += b.actual_tgna ?? 0;
      (b.approval_nos ?? []).forEach((n) => {
        if (!day.approval_nos.includes(n)) day.approval_nos.push(n);
      });
      if (b.time_block != null) {
        day.blocks.push({
          block: b.time_block,
          time: b.time,
          scheduled_mwh: b.scheduled_mwh,
          actual_mwh: b.actual_mwh,
          delta_mwh: b.delta_mwh,
          delta_pct: b.delta_pct,
          actual_gna: b.actual_gna,
          actual_tgna: b.actual_tgna,
        });
      }
    });
    return Object.values(map).map((day) => ({
      ...day,
      delta_mwh: +(day.actual_mwh - day.scheduled_mwh).toFixed(2),
      delta_pct: day.scheduled_mwh
        ? +(((day.actual_mwh - day.scheduled_mwh) / day.scheduled_mwh) * 100).toFixed(1)
        : 0,
    }));
  }
  return data.map((item) => ({
    ...item,
    date: item.date ?? item.week_start ?? item.month ?? item.period ?? "—",
    delta_mwh:
      item.delta_mwh ??
      +((item.actual_mwh ?? 0) - (item.scheduled_mwh ?? 0)).toFixed(2),
    delta_pct:
      item.delta_pct ??
      (item.scheduled_mwh
        ? +(
            ((item.actual_mwh - item.scheduled_mwh) / item.scheduled_mwh) *
            100
          ).toFixed(1)
        : 0),
    blocks: [],
  }));
};

/* ── Delta helpers ──────────────────────────────────────── */
const DeltaVal = ({ val }) => {
  if (val == null) return <span>—</span>;
  return (
    <span style={{ color: val < 0 ? "var(--error-text, #e53935)" : "var(--secondary-color-text)" }}>
      {val > 0 ? "+" : ""}
      {fmt(val)}
    </span>
  );
};

const DeltaPct = ({ val }) => {
  if (val == null) return <span>—</span>;
  return (
    <span style={{ color: val < 0 ? "var(--error-text, #e53935)" : "var(--secondary-color-text)" }}>
      {val > 0 ? "+" : ""}
      {fmt(val, 1)}%
    </span>
  );
};

/* ── Summary Card ───────────────────────────────────────── */
const SummaryCard = ({ label, value, unit, sub, dotColor }) => (
  <div className={Styles.summaryCard}>
    <div className={Styles.summaryCardTop}>
      <span className={Styles.summaryCardLabel}>{label}</span>
      <span className={Styles.summaryCardDot} style={{ background: dotColor }} />
    </div>
    <div className={Styles.summaryCardValue}>
      {value} <span className={Styles.summaryCardUnit}>{unit}</span>
    </div>
    {sub != null && <div className={Styles.summaryCardSub}>{sub}</div>}
  </div>
);

/* ── Legend Chip ────────────────────────────────────────── */
/* Ported .line-legend look from insights ReMixChart: rounded clickable chip
   with a dot swatch (bar series) or short line swatch (line series). When the
   series is toggled off the swatch + text grey to #E0E0E0, matching the ref. */
const ChartLegendChip = ({ label, type, color, active, onClick }) => {
  const swatchColor = active ? color : "#E0E0E0";
  return (
    <div
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        fontSize: 12,
        cursor: "pointer",
        borderRadius: 6,
        padding: "4px 8px",
        border: "0.5px solid #dfeaf2",
        background: "#fff",
        userSelect: "none",
        color: swatchColor,
        transition: "color 0.15s",
      }}
    >
      {type === "bar" ? (
        <span
          style={{
            width: 8,
            height: 8,
            marginRight: 6,
            borderRadius: "50%",
            background: swatchColor,
            display: "inline-block",
            flexShrink: 0,
          }}
        />
      ) : (
        <span
          style={{
            width: 20,
            height: 2,
            marginRight: 6,
            background: swatchColor,
            display: "inline-block",
            verticalAlign: "middle",
            flexShrink: 0,
          }}
        />
      )}
      {label}
    </div>
  );
};

/* ── Chart Section ──────────────────────────────────────── */
const AggregateChartSection = ({ title, groupBy, onGroupByChange, loading, data }) => {
  const isBlockMode = groupBy === "block";
  const safe = data ?? [];
  const totalPoints = safe.length;
  const chartInstanceRef = useRef(null);
  const safeRef = useRef(safe); // ← always current, no stale closure
  const [activeSeries, setActiveSeries] = useState("both");

  // Keep ref in sync every render
  safeRef.current = safe;

  /* ── Dynamic x-axis tick density: zoom tracking ───────────
     lastZoom (ref) updates instantly on every datazoom (no rerender);
     zoomRange (state) is debounced and only triggers the interval recompute.
     Never push zoomRange back into dataZoom.start/end — that fights ECharts
     mid-drag and causes jitter. */
  const lastZoom = useRef(null); // { start, end } in 0–100 percent
  const [zoomRange, setZoomRange] = useState(null);
  const zoomDebounce = useRef(null);
  const skipNextZoom = useRef(false);
  const isDragging = useRef(false);
  const dragEndTimer = useRef(null);
  const stableOptionRef = useRef(null);

  const showActual = activeSeries === "both" || activeSeries === "actual";
  const showScheduled = activeSeries === "both" || activeSeries === "scheduled";

  const toggleSeries = (name) =>
    setActiveSeries((prev) => (prev === name ? "both" : name));

  const useBlockWindow = isBlockMode && totalPoints > 96;
  const initStartValue = Math.max(0, totalPoints - 96);

  // Default window as 0–100 percent (the pattern works in percent space).
  const defaultStartPercent = useBlockWindow
    ? (initStartValue / Math.max(1, totalPoints - 1)) * 100
    : 0;
  const defaultEndPercent = 100;

  /* ── datazoom handler: instant ref update + debounced state ── */
  const handleDataZoom = useCallback((params) => {
    if (skipNextZoom.current) {
      skipNextZoom.current = false;
      return;
    }
    isDragging.current = true;
    if (dragEndTimer.current) clearTimeout(dragEndTimer.current);
    dragEndTimer.current = setTimeout(() => {
      isDragging.current = false;
    }, 200);

    const zoom = params.batch ? params.batch[0] : params;
    const start = zoom.start ?? 0;
    const end = zoom.end ?? 100;
    lastZoom.current = { start, end };

    if (zoomDebounce.current) clearTimeout(zoomDebounce.current);
    zoomDebounce.current = setTimeout(() => setZoomRange({ start, end }), 250);
  }, []);

  /* ── Reset zoom whenever data / mode changes ── */
  useEffect(() => {
    lastZoom.current = null;
    setZoomRange(null);
    skipNextZoom.current = true;
  }, [data, isBlockMode]);

  /* ── Compute the smart, window-aware x-axis interval ──────
     lastZoom (instant) feeds start/end; zoomRange (debounced) is read so a
     settled drag triggers the recompute. visibleDataCount → tick density. */
  const totalLen = totalPoints || 1;
  const zoomStart = lastZoom.current?.start ?? defaultStartPercent;
  const zoomEnd = lastZoom.current?.end ?? defaultEndPercent;
  const visibleStart = zoomRange?.start ?? zoomStart;
  const visibleEnd = zoomRange?.end ?? zoomEnd;

  // Window bounds in global array indices (used both for tick selection and to
  // re-base the window-relative formatter index — see
  // [[echarts-category-formatter-index]]).
  const windowStartIndex = Math.round(
    (visibleStart / 100) * Math.max(0, totalLen - 1),
  );
  const windowEndIndex = Math.round(
    (visibleEnd / 100) * Math.max(0, totalLen - 1),
  );

  // Distinct days visible (in order) → drives the adaptive tick tier. Each day
  // has 96 fifteen-minute blocks; the block value lives on d.x.
  const visibleDateList = [];
  const seenDates = new Set();
  for (let i = windowStartIndex; i <= windowEndIndex && i < totalLen; i++) {
    const dt = safe[i]?.date;
    if (dt && !seenDates.has(dt)) {
      seenDates.add(dt);
      visibleDateList.push(dt);
    }
  }
  const numVisibleDays = visibleDateList.length || 1;
  // Each visible date → its ordinal (0,1,2,…) so we can thin dates when wide.
  const dateOrdinal = new Map(visibleDateList.map((d, i) => [d, i]));

  /* ── Adaptive tick tiers (single intelligent axis) ────────
     WIDE   (≥16 days): dates only — NO block numbers.
     MEDIUM (3–15 days): date is the parent; show 6-hourly blocks.
     ZOOMED (1–2 days):  date is the parent; every block / hourly. */
  const TIER_WIDE = "wide";
  const TIER_MEDIUM = "medium";
  const TIER_ZOOMED = "zoomed";
  const tier =
    numVisibleDays >= 16
      ? TIER_WIDE
      : numVisibleDays >= 3
        ? TIER_MEDIUM
        : TIER_ZOOMED;

  // Wide: thin dates so labels don't smear.
  const dateStep =
    numVisibleDays <= 20
      ? 2
      : numVisibleDays <= 45
        ? 4
        : Math.ceil(numVisibleDays / 10);

  // Block-step sizing ported from insights ReMixChart's computeSmartXInterval:
  // the tick step scales *continuously* with the number of points actually
  // visible in the zoom window (per day), instead of snapping to a fixed grid.
  // This spreads ticks more robustly as you zoom — the visible count drives the
  // density smoothly rather than jumping between coarse tiers.
  const visiblePointCount = Math.max(1, windowEndIndex - windowStartIndex + 1);
  // Points visible within a single day of the current window.
  const visiblePerDay = Math.max(1, Math.round(visiblePointCount / numVisibleDays));
  const computeBlockStep = (count) => {
    if (count <= 15) return 1; // every block — plenty of room
    if (count <= 30) return Math.max(1, Math.floor(count / 8) - 1);
    if (count <= 60) return Math.max(1, Math.floor(count / 8) - 1);
    if (count <= 90) return Math.max(1, Math.floor(count / 10) - 1);
    if (count <= 180) return Math.max(1, Math.floor(count / 12) - 1);
    return Math.max(1, Math.floor(count / 12) - 1);
  };
  const blockStep =
    tier === TIER_MEDIUM ? 24 : computeBlockStep(visiblePerDay);

  // Single day in view: render the date as a centered axis title (parent header).
  const singleDayDate =
    tier === TIER_ZOOMED && numVisibleDays === 1
      ? (visibleDateList[0] ?? "")
      : "";

  // Helper: is the point at global index gi the first visible point of its day?
  const isDayStart = (snapshot, gi) =>
    snapshot[gi]?.date &&
    (gi === windowStartIndex ||
      snapshot[gi].date !== snapshot[gi - 1]?.date);

  const lastXIndex = Math.max(0, safe.length - 1);
  const xAxisIntervalFn = (index) => {
    if (index === 0 || index === lastXIndex) return true;
    const item = safe[index];
    if (!item) return false;

    if (tier === TIER_WIDE) {
      if (!isDayStart(safe, index)) return false;
      const ord = dateOrdinal.get(item.date) ?? 0;
      return ord % dateStep === 0;
    }

    if (isDayStart(safe, index)) return true;
    const blockNum = Number(item.x);
    if (!Number.isFinite(blockNum)) return false;
    return (blockNum - 1) % blockStep === 0;
  };

  const xAxisLabelFormatter = (value, index) => {
    const snapshot = safeRef.current;
    const gi = windowStartIndex + index;
    const item = snapshot[gi];
    if (!item) return "";
    const dayStart = isDayStart(snapshot, gi);

    if (tier === TIER_WIDE) {
      return dayStart ? `{d|${item.date}}` : "";
    }

    if (singleDayDate) return String(value ?? "");

    return dayStart ? `${value}\n{d|${item.date}}` : String(value ?? "");
  };

  const computedOptions = {
    grid: { top: 8, right: 16, bottom: isBlockMode ? 88 : 68, left: 52 },
    xAxis: {
      type: "category",
      data: safe.map((d) => d.x),
      // Axis line + ticks styled to match insights ReMixChart.
      axisLine: { lineStyle: { color: AXIS_LINE_COLOR } },
      axisTick: {
        show: true,
        alignWithLabel: true,
        length: 4,
        lineStyle: { color: AXIS_LINE_COLOR },
      },
      boundaryGap: true,
      axisLabel: {
        fontSize: 10,
        color: AXIS_LABEL_COLOR,
        margin: 12,
        showMinLabel: true,
        showMaxLabel: true,
        hideOverlap: false,
        ...(isBlockMode
          ? { interval: xAxisIntervalFn, formatter: xAxisLabelFormatter }
          : {}),
        rich: { d: { fontSize: 9, color: "#888", lineHeight: 16 } },
      },
      // Single day in view → date as a centered parent header under the axis.
      ...(singleDayDate
        ? {
            name: singleDayDate,
            nameLocation: "middle",
            nameGap: 32,
            nameTextStyle: { fontSize: 11, color: SECONDARY_COLOR, fontWeight: 600 },
          }
        : {}),
    },
    yAxis: {
      type: "value",
      splitNumber: 5,
      scale: false,
      boundaryGap: [0, 0.1],
      axisLine: { show: false },
      axisTick: { show: true, length: 4, lineStyle: { color: AXIS_LINE_COLOR } },
      splitLine: { lineStyle: { color: "#f0f0f0" } },
      axisLabel: {
        fontSize: 10,
        color: AXIS_LABEL_COLOR,
        margin: 8,
        formatter: formatAxisValue,
        hideOverlap: true,
      },
      name: isBlockMode ? "MW" : "MWh",
      nameLocation: "middle",
      nameGap: 40,
      nameRotate: 90,
      nameTextStyle: {
        fontSize: 11,
        color: SECONDARY_COLOR,
        fontWeight: 600,
        fontFamily: "Inter, system-ui, sans-serif",
      },
    },
    tooltip: {
      trigger: "axis",
      triggerOn: "mousemove",
      backgroundColor: "transparent",
      borderColor: "transparent",
      borderWidth: 0,
      padding: 0,
      // Card styling lives in the inline <style> from buildTooltipHTML; keep the
      // ECharts wrapper transparent so only the frosted card shows.
      extraCssText: "box-shadow:none;background:transparent;border:none;padding:0;",
      // Line axis-pointer styled to match the tooltip accent.
      axisPointer: {
        type: "line",
        snap: false,
        lineStyle: { color: SECONDARY_COLOR, width: 1, type: "dashed", opacity: 0.5 },
        label: { show: false },
      },
      formatter: (params) => {
        const idx = params[0]?.dataIndex ?? 0;
        const item = safeRef.current[idx];
        const unit = isBlockMode ? "MW" : "MWh";
        const title =
          isBlockMode && item
            ? `Block ${item.x}`
            : String(params[0]?.axisValue ?? "");
        const subtitle = isBlockMode && item?.date ? item.date : "";
        const rows = params.map((p) => ({
          name: p.seriesName,
          value: p.value,
          color: p.color,
        }));
        return buildTooltipHTML({ title, subtitle, rows, unit });
      },
    },
    legend: { show: false },
    dataZoom: [
      // Never push zoomRange into start/end — only lastZoom/defaults, so we
      // don't fight ECharts during the drag (causes jitter).
      { type: "inside", start: zoomStart, end: zoomEnd, filterMode: "none" },
      {
        // Slider styled to match insights ReMixChart brush exactly: minimal
        // (ECharts defaults) + SecondaryColor text + droplet handle icon.
        type: "slider",
        labelFormatter: (val) => {
          const idx = Math.round(val);
          const arr = safeRef.current;
          if (idx < 0 || idx >= arr.length) return "";
          const d = arr[idx];
          // Slot/block number only — date intentionally omitted.
          return String(d.x ?? "");
        },
        start: zoomStart,
        end: zoomEnd,
        height: 17,
        width: "98%",
        bottom: 10,
        left: 10,
        brushSelect: false,
        textStyle: { color: SECONDARY_COLOR },
        handleIcon:
          "path://M10.7,11.9v-1.3H9.3v1.3c-4.9,0.3-8.8,4.4-8.8,9.4c0,5,3.9,9.1,8.8,9.4v1.3h1.3v-1.3c4.9-0.3,8.8-4.4,8.8-9.4C19.5,16.3,15.6,12.2,10.7,11.9z M13.3,24.4H6.7V23h6.6V24.4z M13.3,19.6H6.7v-1.4h6.6V19.6z",
        handleSize: "80%",
        filterMode: "none",
        throttle: 60,
      },
    ],
    series: [
      {
        name: "Actual",
        type: "bar",
        data: showActual ? safe.map((d) => d.actual_mw) : safe.map(() => null),
        itemStyle: { color: SERIES_COLORS.actual, borderRadius: [2, 2, 0, 0] },
        barMaxWidth: 14,
      },
      {
        name: "Scheduled",
        type: "line",
        data: showScheduled
          ? safe.map((d) => d.scheduled_mw)
          : safe.map(() => null),
        lineStyle: { color: SERIES_COLORS.scheduled, width: 2, type: "solid" },
        itemStyle: { color: SERIES_COLORS.scheduled },
        symbol: "none",
      },
    ],
  };

  /* During an active drag, render the last stable option so notMerge can't
     reinitialize the chart and fight the drag. Cache once drag settles. */
  const chartOptions =
    isDragging.current && stableOptionRef.current
      ? stableOptionRef.current
      : computedOptions;
  if (!isDragging.current) stableOptionRef.current = computedOptions;

  return (
    <div className={Styles.chartSection}>
      <div className={Styles.chartSectionHeader}>
        <span className={Styles.chartTitle}>{title}</span>
        <div className={Styles.periodToggle}>
          {GROUP_BY_OPTIONS.map((g) => (
            <button
              key={g.key}
              className={`${Styles.periodToggleBtn} ${groupBy === g.key ? Styles.periodToggleBtnActive : ""}`}
              onClick={() => onGroupByChange(g.key)}
            >
              {g.label}
            </button>
          ))}
        </div>
      </div>
      <div className={Styles.chartWrapper}>
        {loading ? (
          <div className={Styles.chartPlaceholder}>Loading…</div>
        ) : !safe.length ? (
          <div className={Styles.chartPlaceholder}>No data</div>
        ) : (
          <>
            <ReactECharts
              style={{ width: "100%", height: 290 }}
              option={chartOptions}
              notMerge
              onChartReady={(instance) => { chartInstanceRef.current = instance; }}
              onEvents={{ datazoom: handleDataZoom }}
            />
            {/* Legend — ported .line-legend chip style from insights ReMixChart:
                dot swatch for the bar series, short line for the line series,
                rounded chip, click toggles the series (greys to #E0E0E0). */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                gap: 8,
                marginTop: 6,
              }}
            >
              <ChartLegendChip
                label="Actual"
                type="bar"
                color={SERIES_COLORS.actual}
                active={showActual}
                onClick={() => toggleSeries("actual")}
              />
              <ChartLegendChip
                label="Scheduled"
                type="line"
                color={SERIES_COLORS.scheduled}
                active={showScheduled}
                onClick={() => toggleSeries("scheduled")}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/* ── Aggregate Breakdown Table ──────────────────────────── */
const AggregateBreakdownTable = ({ rows, expandedRows, onToggle, loading }) => {
  if (loading) return <div className={Styles.tableLoading}>Loading…</div>;
  if (!rows?.length) return <div className={Styles.tableLoading}>No data available</div>;

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <span style={{
          fontSize: 11, color: "var(--fifth-color-text)",
          background: "var(--fifth-color-background)",
          padding: "3px 10px", borderRadius: 12, fontWeight: 500,
        }}>
          {rows.length} records
        </span>
      </div>
      <div className={Styles.tableWrapper}>
        <table className={Styles.breakdownTable}>
          <thead>
            <tr>
              <th className={Styles.thCell} style={{ width: 28 }} />
              <th className={Styles.thCell}>DATE</th>
              <th className={Styles.thCell}>SCHEDULED MWH</th>
              <th className={Styles.thCell}>ACTUAL MWH</th>
              <th className={Styles.thCell}>Δ MWH</th>
              <th className={Styles.thCell}>Δ %</th>
              <th className={Styles.thCell}>ACTUAL GNA</th>
              <th className={Styles.thCell}>ACTUAL TGNA</th>
              <th className={Styles.thCell}>APPROVAL NOS.</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const key = row.date ?? i;
              const expanded = expandedRows.has(key);
              const hasBlocks = (row.blocks ?? []).length > 0;
              return (
                <React.Fragment key={key}>
                  <tr
                    className={`${Styles.tdRow} ${expanded ? Styles.tdRowExpanded : ""}`}
                    onClick={() => hasBlocks && onToggle(key)}
                    style={{ cursor: hasBlocks ? "pointer" : "default" }}
                  >
                    <td className={Styles.tdCell}>
                      {hasBlocks ? (
                        expanded ? (
                          <ExpandMore className={Styles.expandIcon} style={{ color: "#1565c0" }} />
                        ) : (
                          <ChevronRight className={Styles.expandIcon} />
                        )
                      ) : null}
                    </td>
                    <td className={`${Styles.tdCell} ${Styles.tdDate}`}>{row.date}</td>
                    <td className={Styles.tdCell}>{fmt(row.scheduled_mwh)}</td>
                    <td className={Styles.tdCell}>{fmt(row.actual_mwh)}</td>
                    <td className={Styles.tdCell}><DeltaVal val={row.delta_mwh} /></td>
                    <td className={Styles.tdCell}><DeltaPct val={row.delta_pct} /></td>
                    <td className={Styles.tdCell}>{fmt(row.actual_gna)}</td>
                    <td className={Styles.tdCell}>{fmt(row.actual_tgna)}</td>
                    <td className={Styles.tdCell}>
                      <div className={Styles.approvalList}>
                        {(row.approval_nos ?? []).map((n, j) => (
                          <span key={j} className={Styles.approvalNo}>{n}</span>
                        ))}
                      </div>
                    </td>
                  </tr>
                  {expanded &&
                    (row.blocks ?? []).map((b, j) => (
                      <tr key={`${key}-b${b.block ?? j}`} className={Styles.blockRow}>
                        <td className={Styles.tdCell} />
                        <td className={Styles.tdCell}>
                          <div className={Styles.blockMeta}>
                            <span className={Styles.blockBadge}>{b.block}</span>
                            <span className={Styles.blockTime}>{b.time}</span>
                          </div>
                        </td>
                        <td className={Styles.tdCell}>{fmt(b.scheduled_mwh)}</td>
                        <td className={Styles.tdCell}>{fmt(b.actual_mwh)}</td>
                        <td className={Styles.tdCell}><DeltaVal val={b.delta_mwh} /></td>
                        <td className={Styles.tdCell}><DeltaPct val={b.delta_pct} /></td>
                        <td className={Styles.tdCell}>{fmt(b.actual_gna)}</td>
                        <td className={Styles.tdCell}>{fmt(b.actual_tgna)}</td>
                        <td className={Styles.tdCell} />
                      </tr>
                    ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};

/* ── Contribution Row ───────────────────────────────────── */
const ContributionRow = ({ dotColor, from, to, leftLabel, leftValue, rightLabel, rightValue, sharePct, variancePct }) => (
  <div style={{
    display: "flex", alignItems: "center", gap: 12,
    padding: "12px 0", borderBottom: "var(--border-level4)",
  }}>
    <span style={{ width: 8, height: 8, borderRadius: "50%", background: dotColor, flexShrink: 0 }} />
    <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 6, minWidth: 0, overflow: "hidden" }}>
      <span style={{ fontSize: 13, fontWeight: 600, color: "var(--third-color-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{from}</span>
      <ArrowRightAlt style={{ fontSize: 16, color: "var(--primary-color-text)", flexShrink: 0 }} />
      <span style={{ fontSize: 13, fontWeight: 600, color: "var(--third-color-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{to}</span>
    </div>
    <div style={{ display: "flex", gap: 24, flexShrink: 0 }}>
      <div style={{ textAlign: "right" }}>
        <div style={{ fontSize: 10, color: "var(--fifth-color-text)", fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", marginBottom: 2 }}>{leftLabel}</div>
        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--third-color-text)" }}>
          {leftValue} <span style={{ fontSize: 11, color: "var(--fifth-color-text)" }}>MWh</span>
        </div>
      </div>
      <div style={{ textAlign: "right" }}>
        <div style={{ fontSize: 10, color: "var(--fifth-color-text)", fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", marginBottom: 2 }}>{rightLabel}</div>
        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--third-color-text)" }}>
          {rightValue} <span style={{ fontSize: 11, color: "var(--fifth-color-text)" }}>MWh</span>
        </div>
      </div>
      <div style={{ textAlign: "right" }}>
        <div style={{ fontSize: 10, color: "var(--fifth-color-text)", fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", marginBottom: 2 }}>Share</div>
        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--third-color-text)" }}>
          {sharePct != null ? `${fmt(sharePct, 1)}%` : "—"}
        </div>
      </div>
      <div style={{ textAlign: "right" }}>
        <div style={{ fontSize: 10, color: "var(--fifth-color-text)", fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", marginBottom: 2 }}>Variance</div>
        <div style={{ fontSize: 13, fontWeight: 700, color: variancePct != null && Number(variancePct) >= 0 ? "var(--secondary-color-text)" : "var(--error-text, #e53935)" }}>
          {variancePct != null ? `${Number(variancePct) >= 0 ? "+" : ""}${fmt(variancePct, 2)}%` : "—"}
        </div>
      </div>
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════════════════════ */
const AggregateViewContainer = () => {
  const [overview, setOverview] = useState(null);
  const [supplyChartData, setSupplyChartData] = useState([]);
  const [supplyBreakdown, setSupplyBreakdown] = useState([]);
  const [returnChartData, setReturnChartData] = useState([]);
  const [returnBreakdown, setReturnBreakdown] = useState([]);
  const [returnMetrics, setReturnMetrics] = useState(null);
  const [supplySources, setSupplySources] = useState([]);
  const [supplyDestinations, setSupplyDestinations] = useState([]);
  const [supplyGroupBy, setSupplyGroupBy] = useState("block");
  const [returnGroupBy, setReturnGroupBy] = useState("block");
  const [supplyLoading, setSupplyLoading] = useState(false);
  const [returnLoading, setReturnLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [expandedReturnRows, setExpandedReturnRows] = useState(new Set());

  useEffect(() => {
    getCumulativeOverview().then((res) => {
      const d = res?.data?.data;
      setOverview(d && typeof d === "object" && !Array.isArray(d) ? d : null);
    });
    getCumulativeSupplySources().then((res) => {
      const d = res?.data?.data;
      setSupplySources(Array.isArray(d) ? d : []);
    });
    getCumulativeSupplyDestinations().then((res) => {
      const d = res?.data?.data;
      setSupplyDestinations(Array.isArray(d) ? d : []);
    });
  }, []);

  useEffect(() => {
    setSupplyLoading(true);
    setExpandedRows(new Set());
    getCumulativeSupply(supplyGroupBy).then((res) => {
      const data = extractArray(res);
      setSupplyChartData(toChartData(data));
      setSupplyBreakdown(processBreakdown(data, supplyGroupBy));
      setSupplyLoading(false);
    });
  }, [supplyGroupBy]);

  useEffect(() => {
    setReturnLoading(true);
    setExpandedReturnRows(new Set());
    getCumulativeReturn(returnGroupBy).then((res) => {
      const data = extractArray(res);
      const metrics = extractMetrics(res);
      setReturnChartData(toChartData(data));
      setReturnBreakdown(processBreakdown(data, returnGroupBy));
      if (metrics) setReturnMetrics(metrics);
      setReturnLoading(false);
    });
  }, [returnGroupBy]);

  const toggleRow = (key) =>
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const toggleReturnRow = (key) =>
    setExpandedReturnRows((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  /* ── Derived values ── */
  const totalScheduled = overview?.total_scheduled_mwh;
  const totalActual = overview?.total_actual_mwh;
  const netVariancePct = overview?.net_variance_pct;
  const avgBankingFactor = overview?.avg_banking_factor_pct;

  const returnRequired =
    overview?.return_required_mwh ??
    (totalActual != null && avgBankingFactor != null
      ? totalActual * (avgBankingFactor / 100)
      : null);

  const returnGiven =
    returnMetrics?.total_actual_mwh ?? overview?.total_return_actual_mwh ?? 0;
  const returnScheduled =
    returnMetrics?.total_scheduled_mwh ?? overview?.total_return_scheduled_mwh ?? returnRequired;
  const returnLeft = returnRequired != null ? returnRequired - returnGiven : null;
  const returnCoverage =
    returnRequired ? (returnGiven / returnRequired) * 100 : 0;

  return (
    <div className={Styles.viewContainer}>
      {/* ── Header ── */}
      <div className={Styles.viewHeader}>
        <div className={Styles.viewHeaderLeft}>
          <div style={{ fontSize: 16, fontWeight: 800, color: "var(--third-color-text)" }}>
            All contracts
          </div>
          {overview && (
            <div className={Styles.viewMeta}>
              <span className={Styles.viewMetaItem}>
                Contracts combined <strong>{overview.contracts_combined ?? "—"}</strong>
              </span>
              <span className={Styles.viewMetaSep}>·</span>
              <span className={Styles.viewMetaItem}>
                Avg banking factor{" "}
                <strong>{avgBankingFactor != null ? `${avgBankingFactor}%` : "—"}</strong>
              </span>
              <span className={Styles.viewMetaSep}>·</span>
              <span className={Styles.viewMetaItem}>
                Drawing supply <strong>{overview.drawing_supply ?? "—"}</strong>
              </span>
              <span className={Styles.viewMetaSep}>·</span>
              <span className={Styles.viewMetaItem}>
                Returning power <strong>{overview.returning_power ?? "—"}</strong>
              </span>
              <span className={Styles.viewMetaSep}>·</span>
              <span className={Styles.viewMetaItem}>
                Giving supply <strong>{overview.giving_supply ?? "—"}</strong>
              </span>
              <span className={Styles.viewMetaSep}>·</span>
              <span className={Styles.viewMetaItem}>
                Receiving back <strong>{overview.receiving_back ?? "—"}</strong>
              </span>
              {netVariancePct != null && (
                <>
                  <span className={Styles.viewMetaSep}>·</span>
                  <span className={Styles.viewMetaItem}>
                    Net variance{" "}
                    <strong
                      style={{
                        color:
                          netVariancePct < 0
                            ? "var(--error-text, #e53935)"
                            : "var(--secondary-color-text)",
                      }}
                    >
                      {netVariancePct > 0 ? "+" : ""}
                      {fmt(netVariancePct, 2)}%
                    </strong>
                  </span>
                </>
              )}
            </div>
          )}
        </div>
        <button className={Styles.exportBtn}>
          <FileDownload style={{ fontSize: "14px" }} /> Export
        </button>
      </div>

      {/* ── Scrollable content ── */}
      <div className={`${Styles.overviewContent} scroll_bar_y`}>
        {/* Supply summary — 3 cards */}
        <div className={Styles.summaryGrid} style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
          <SummaryCard
            label="TOTAL SCHEDULED"
            value={fmt(totalScheduled, 0)}
            unit="MWh"
            dotColor="#2E90FA"
          />
          <SummaryCard
            label="TOTAL ACTUAL"
            value={fmt(totalActual, 2)}
            unit="MWh"
            sub={
              netVariancePct != null ? (
                <span style={{ color: netVariancePct < 0 ? "var(--error-text, #e53935)" : "var(--secondary-color-text)" }}>
                  {netVariancePct > 0 ? "+" : ""}
                  {fmt(netVariancePct, 2)}% vs scheduled
                </span>
              ) : null
            }
            dotColor="#ff9800"
          />
          <SummaryCard
            label="RETURN REQUIRED"
            value={fmt(returnRequired, 2)}
            unit="MWh"
            dotColor="#9c27b0"
          />
        </div>

        {/* Supply chart */}
        <AggregateChartSection
          title="Cumulative supply: actual vs scheduled"
          groupBy={supplyGroupBy}
          onGroupByChange={setSupplyGroupBy}
          loading={supplyLoading}
          data={supplyChartData}
        />

        {/* Contribution by contract */}
        {supplySources.length > 0 && (
          <div className={Styles.breakdownSection}>
            <div className={Styles.breakdownTitle}>Contribution by contract</div>
            <div className={Styles.breakdownSub}>How each contract weighs into the aggregate</div>
            {supplySources.map((src, i) => (
              <ContributionRow
                key={i}
                dotColor={CONTRACT_COLORS[i % CONTRACT_COLORS.length]}
                from={src.supplier ?? src.from ?? "—"}
                to={src.receiver ?? src.to ?? "—"}
                leftLabel="Scheduled Supply"
                leftValue={fmt(src.scheduled_supply_mwh ?? src.scheduled_mwh, 0)}
                rightLabel="Actual Supply"
                rightValue={fmt(src.actual_supply_mwh ?? src.actual_mwh, 2)}
                sharePct={src.share_pct}
                variancePct={src.variance_pct}
              />
            ))}
          </div>
        )}

        {/* Supply breakdown */}
        <div className={Styles.breakdownSection}>
          <div className={Styles.breakdownTitle}>Supply breakdown</div>
          <div className={Styles.breakdownSub}>Click a row to drill into 15-minute blocks</div>
          <AggregateBreakdownTable
            rows={supplyBreakdown}
            expandedRows={expandedRows}
            onToggle={toggleRow}
            loading={supplyLoading}
          />
        </div>

        {/* Return divider */}
        <div className={Styles.returnDivider}>
          <span className={Styles.returnDividerLine} />
          <span className={Styles.returnDividerLabel}>RETURN</span>
          <span className={Styles.returnDividerLine} />
        </div>

        {/* Return summary — 4 cards */}
        <div className={Styles.summaryGrid}>
          <SummaryCard
            label="RETURN REQUIRED"
            value={fmt(returnRequired, 2)}
            unit="MWh"
            dotColor="#9c27b0"
          />
          <SummaryCard
            label="RETURN GIVEN"
            value={fmt(returnGiven, 0)}
            unit="MWh"
            sub={returnScheduled != null ? `Scheduled ${fmt(returnScheduled, 0)} MWh` : null}
            dotColor="#26a69a"
          />
          <SummaryCard
            label="RETURN LEFT"
            value={fmt(returnLeft, 2)}
            unit="MWh"
            dotColor="#f44336"
          />
          <SummaryCard
            label="COVERAGE"
            value={fmt(returnCoverage, 1)}
            unit="%"
            dotColor="#ffc107"
          />
        </div>

        {/* Return chart */}
        <AggregateChartSection
          title="Cumulative return: actual vs scheduled"
          groupBy={returnGroupBy}
          onGroupByChange={setReturnGroupBy}
          loading={returnLoading}
          data={returnChartData}
        />

        {/* Return by contract */}
        {supplyDestinations.length > 0 && (
          <div className={Styles.breakdownSection}>
            <div className={Styles.breakdownTitle}>Return by contract</div>
            <div className={Styles.breakdownSub}>How each contract contributes to the return</div>
            {supplyDestinations.map((dest, i) => (
              <ContributionRow
                key={i}
                dotColor={CONTRACT_COLORS[i % CONTRACT_COLORS.length]}
                from={dest.receiver ?? dest.from ?? "—"}
                to={dest.supplier ?? dest.to ?? "—"}
                leftLabel="Scheduled Return"
                leftValue={fmt(dest.scheduled_return_mwh ?? dest.scheduled_mwh, 0)}
                rightLabel="Actual Return"
                rightValue={fmt(dest.actual_return_mwh ?? dest.actual_mwh, 2)}
                sharePct={dest.share_pct}
                variancePct={dest.variance_pct}
              />
            ))}
          </div>
        )}

        {/* Return breakdown */}
        <div className={Styles.breakdownSection}>
          <div className={Styles.breakdownTitle}>Return breakdown</div>
          <div className={Styles.breakdownSub}>Click a row to drill into 15-minute blocks</div>
          <AggregateBreakdownTable
            rows={returnBreakdown}
            expandedRows={expandedReturnRows}
            onToggle={toggleReturnRow}
            loading={returnLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default AggregateViewContainer;
